import type {
  ToastManagerAddOptions,
  ToastManagerPromiseOptions,
  ToastObject,
} from '@base-ui/react/toast';
import { Toast } from '@base-ui/react/toast';
export { Toast } from '@base-ui/react/toast';
import type React from 'react';

import IconAlertOctagonFilled from '@tabler/icons-react/dist/esm/icons/IconAlertOctagonFilled.mjs';
import IconAlertTriangleFilled from '@tabler/icons-react/dist/esm/icons/IconAlertTriangleFilled.mjs';
import IconCircleCheckFilled from '@tabler/icons-react/dist/esm/icons/IconCircleCheckFilled.mjs';
import IconInfoCircleFilled from '@tabler/icons-react/dist/esm/icons/IconInfoCircleFilled.mjs';
import IconX from '@tabler/icons-react/dist/esm/icons/IconX.mjs';

import { cn } from '../../utils/cn';
import { usePortalContainer, type PortalContainer } from '../../utils/portal-provider';
import { Box, Col, Row } from '../box/box';
import type { ButtonProps } from '../button/button';
import { Button } from '../button/button';

/**
 * Toast styling configuration for Figma plugin consumption.
 * Toast has no user-facing variants but documents the styling structure.
 */
export const ATOMS_TOAST_VARIANTS = {
  root: {
    classes:
      'rounded-xl ring ring-od-border-tertiary bg-od-bg-inverse-primary p-4 shadow-lg text-od-content-inverse-primary',
    description: 'Toast container with inverse background, ring, and shadow',
  },
  title: {
    classes: 'text-label-md text-od-content-inverse-primary',
    description: 'Toast title with primary text color',
  },
  description: {
    classes: 'text-body-sm text-od-content-inverse-secondary',
    description: 'Toast description with muted text color',
  },
  close: {
    classes:
      'absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded bg-transparent text-od-content-inverse-secondary hover:bg-od-bg-inverse-hover-primary hover:text-od-content-inverse-primary',
    description: 'Close button with X icon',
  },
  variant: {
    default: {
      classes: 'ring-od-border-tertiary bg-od-bg-inverse-primary',
      description: 'Default toast style',
    },
    success: {
      classes:
        'ring-[0.3px] ring-od-border-success bg-od-bg-success [&_[data-toast-icon]]:text-od-content-inverse-primary [&_[data-toast-title]]:text-od-content-inverse-primary',
      description: 'Success toast for confirmations and positive outcomes',
      icon: IconCircleCheckFilled,
    },
    error: {
      classes:
        'ring-[0.3px] ring-od-border-error bg-od-bg-error [&_[data-toast-icon]]:text-od-content-inverse-primary [&_[data-toast-title]]:text-od-content-inverse-primary',
      description: 'Error toast for critical issues',
      icon: IconAlertOctagonFilled,
    },
    warning: {
      classes:
        'ring-[0.3px] ring-od-border-error bg-od-bg-error [&_[data-toast-icon]]:text-od-content-inverse-primary [&_[data-toast-title]]:text-od-content-inverse-primary',
      description: 'Warning toast for cautionary messages',
      icon: IconAlertTriangleFilled,
    },
    info: {
      classes:
        'ring-[0.3px] ring-od-border-accent-brand bg-od-bg-accent-brand [&_[data-toast-icon]]:text-od-content-inverse-primary [&_[data-toast-title]]:text-od-content-inverse-primary',
      description: 'Info toast for neutral informational messages',
      icon: IconInfoCircleFilled,
    },
  },
} as const;

export const ATOMS_TOAST_DEFAULT_VARIANTS = {
  variant: 'default',
} as const;

/**
 * Toast styling configuration for Figma plugin consumption.
 * Provides structured metadata for generating Toast components in Figma.
 */
export const ATOMS_TOAST_STYLING = {
  container: {
    width: 340, // viewport sm:w-85
    padding: 16, // p-4
    borderRadius: 12, // rounded-xl
    background: 'bg-od-bg-inverse-primary',
    border: 'ring ring-od-border-tertiary',
    shadow: 'shadow-lg',
    gap: 4, // gap-1
  },
  title: {
    fontSize: 16, // text-label-md
    fontWeight: 500,
    color: 'text-od-content-inverse-primary',
  },
  description: {
    fontSize: 14, // text-body-sm
    fontWeight: 400,
    color: 'text-od-content-inverse-secondary',
  },
  // Variant leading icons (h-4 w-4); the default variant renders no icon
  icons: [
    { name: 'tabler-circle-check-filled', state: 'success', size: 16 },
    { name: 'tabler-alert-octagon-filled', state: 'error', size: 16 },
    { name: 'tabler-alert-triangle-filled', state: 'warning', size: 16 },
    { name: 'tabler-info-circle-filled', state: 'info', size: 16 },
  ],
  closeButton: {
    size: 20, // h-5 w-5
    iconSize: 16, // h-4 w-4
    iconName: 'tabler-x',
    iconColor: 'text-od-content-inverse-secondary',
    hoverBackground: 'bg-od-bg-inverse-hover-primary',
    hoverColor: 'text-od-content-inverse-primary',
    borderRadius: 4, // rounded
  },
} as const;

// Derived types from ATOMS_TOAST_VARIANTS
export type AtomsToastVariant = keyof typeof ATOMS_TOAST_VARIANTS.variant;

export interface AtomsToastVariantsProps {
  variant?: AtomsToastVariant;
}

export function toastVariants({
  variant = ATOMS_TOAST_DEFAULT_VARIANTS.variant,
}: AtomsToastVariantsProps = {}) {
  return cn(
    // Base styles for toast root. The base text color must live here so custom
    // `content` (which renders no Toast.Title/Description) stays legible on the
    // dark inverse background instead of inheriting the ambient page color.
    'rounded-xl ring ring-od-border-tertiary bg-clip-padding p-4 shadow-lg text-od-content-inverse-primary',
    // Apply variant styles from ATOMS_TOAST_VARIANTS
    ATOMS_TOAST_VARIANTS.variant[variant].classes,
  );
}

/**
 * Toasty component props.
 *
 * Wrap your app with `<Toasty>` to enable toast notifications.
 * Use `Toast.useToastManager().notify(…)` to create toasts.
 *
 * @example
 * ```tsx
 * // 1. Wrap your app
 * <Toasty>
 *   <App />
 * </Toasty>
 *
 * // 2. Show a toast from any child component
 * const toasts = Toast.useToastManager();
 * toasts.notify({ title: "Saved", description: "Changes saved successfully." });
 * ```
 */
export interface ToastyProps extends AtomsToastVariantsProps {
  /** Application content. Toasts render via a portal above this. */
  children: React.ReactNode;
  /**
   * Container element for the portal. Use this to render toasts inside
   * a Shadow DOM or custom container. Overrides `AtomsPortalProvider` context.
   * @default document.body (or AtomsPortalProvider container if set)
   */
  container?: PortalContainer;
}

type AtomsToastOptionsBase = {
  variant?: AtomsToastVariant;
  content?: React.ReactNode;
  actions?: Array<ButtonProps>;
  bump?: boolean;
};

export type AtomsToastOptions<Data extends object> = ToastObject<Data> & AtomsToastOptionsBase;

export type AtomsToastManagerAddOptions<Data extends object> = ToastManagerAddOptions<Data> &
  AtomsToastOptionsBase;

// Base UI requires a toast's custom `data` payload to be an object; atoms toasts
// don't use it, so callers default to an open record.
type DefaultToastData = Record<string, unknown>;

// The slice of Base UI's toast manager (returned by `useToastManager` /
// `createToastManager`) that the wrapper builds on. Methods accept the
// atoms-extended options because Base UI preserves the extra top-level fields
// (variant/content/actions/bump) on the stored toast; `toasts` is optional since
// `createToastManager` doesn't expose the array directly.
interface WrappableToastManager<Data extends object> {
  add: (options: AtomsToastManagerAddOptions<Data>) => string;
  update: (id: string, options: Partial<AtomsToastManagerAddOptions<Data>>) => void;
  promise: <Value>(
    promise: Promise<Value>,
    options: ToastManagerPromiseOptions<Value, Data>,
  ) => Promise<Value>;
  toasts?: ReadonlyArray<ToastObject<Data>>;
}

function wrapManagerMethods<Data extends object>(manager: WrappableToastManager<Data>) {
  return {
    ...manager,

    add: (options: AtomsToastManagerAddOptions<Data>) => {
      // Capture to a const so the `string` narrowing persists into the
      // requestAnimationFrame callback below (TS keeps const narrowing in closures).
      const { id } = options;
      if (id) {
        const toasts = manager.toasts;

        if (toasts) {
          const existingToast = toasts.find((toast) => toast.id === id);

          // If toast exists and is not exiting, trigger bump and prevent duplicate
          if (existingToast && existingToast.transitionStatus !== 'ending') {
            // Reset animation by disabling then re-enabling
            manager.update(id, { bump: false });
            requestAnimationFrame(() => {
              manager.update(id, {
                bump: true,
                ...(options.timeout !== undefined && {
                  timeout: options.timeout,
                }),
              });
            });
            return id;
          }

          // If toast exists and is exiting, let it finish - don't add duplicate
          if (existingToast && existingToast.transitionStatus === 'ending') {
            return id;
          }
        }
      }

      return manager.add({
        ...options,
      });
    },

    update: (id: string, options: Partial<AtomsToastManagerAddOptions<Data>>) => {
      return manager.update(id, {
        ...options,
      });
    },

    promise: <TData,>(
      promise: Promise<TData>,
      options: {
        loading: AtomsToastManagerAddOptions<Data>;
        success:
          | AtomsToastManagerAddOptions<Data>
          | ((data: TData) => AtomsToastManagerAddOptions<Data>);
        error:
          | AtomsToastManagerAddOptions<Data>
          | ((error: Error) => AtomsToastManagerAddOptions<Data>);
      },
    ) => {
      // Destructure to consts so the `typeof === 'function'` narrowing persists
      // into the callbacks below (TS keeps const narrowing inside closures).
      const { loading, success, error } = options;
      return manager.promise(promise, {
        loading: { ...loading },
        success:
          typeof success === 'function' ? (data: TData) => ({ ...success(data) }) : { ...success },
        error:
          typeof error === 'function'
            ? (errorArg: Error) => ({ ...error(errorArg) })
            : { ...error },
      });
    },
  };
}

export const useAtomsToastManager = <Data extends object = DefaultToastData>() => {
  const manager = Toast.useToastManager<Data>();
  // The atoms fields are optional, so Base UI's toasts widen to the richer shape
  // without an assertion; reading them is typed (and `undefined` when absent).
  const toasts: Array<AtomsToastOptions<Data>> = manager.toasts;
  return {
    ...wrapManagerMethods<Data>(manager),
    toasts,
  };
};

export const createAtomsToastManager = <Data extends object = DefaultToastData>() => {
  return wrapManagerMethods<Data>(Toast.createToastManager<Data>());
};

/**
 * Toasty — toast notification provider and viewport.
 *
 * Renders a `Toast.Provider` with a fixed-position viewport at the bottom right.
 * Toasts stack with smooth enter/exit animations, swipe-to-dismiss, and expand-on-hover.
 *
 * Built on `@base-ui/react/toast`.
 *
 * @example
 * ```tsx
 * <Toasty>
 *   <App />
 * </Toasty>
 * ```
 */
export function Toasty({ children, container: containerProp }: ToastyProps) {
  const contextContainer = usePortalContainer();
  const container = containerProp ?? contextContainer ?? undefined;

  return (
    <Toast.Provider>
      {children}
      <Toast.Portal container={container}>
        <Toast.Viewport className="fixed top-auto right-4 bottom-4 z-1 flex w-[calc(100%-2rem)] sm:right-8 sm:bottom-8 sm:w-85">
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  );
}

/** Alias for Toasty — provided for discoverability when migrating from other libraries */
export const ToastProvider = Toasty;

function ToastList() {
  const { toasts } = useAtomsToastManager();
  return toasts.map((toast) => (
    <Toast.Root
      key={toast.id}
      toast={toast}
      className={cn(
        'absolute right-0 bottom-0 left-auto z-[calc(1000-var(--toast-index))] h-(--height) w-full origin-bottom select-none',
        toastVariants({ variant: toast.variant }),
        '[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(calc(var(--toast-offset-y)*-1)-calc(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]',
        'transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]',
        "after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
        'data-ending-style:opacity-0 data-expanded:h-(--toast-height) data-expanded:transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--offset-y)))] data-limited:opacity-0 data-starting-style:transform-[translateY(150%)]',
        'data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+150%))] data-expanded:data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+150%))]',
        'data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))] data-expanded:data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]',
        'data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))] data-expanded:data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
        'data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))] data-expanded:data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))]',
        '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:transform-[translateY(150%)]',
        toast.bump && 'animate-toast-bump',
      )}
    >
      <ToastBackground variant={toast.variant} />
      <Toast.Content className="isolate flex flex-col gap-1 transition-opacity duration-250 data-behind:pointer-events-none data-behind:opacity-0 data-expanded:pointer-events-auto data-expanded:opacity-100">
        {toast.content ?? (
          <Row className="items-start gap-2">
            <ToastIcon variant={toast.variant} />
            <Col className="gap-1 overflow-hidden">
              <Toast.Title
                data-toast-title
                className="text-label-md text-od-content-inverse-primary"
              />
              <Toast.Description className="text-body-sm text-od-content-inverse-secondary" />

              {!!toast.actions && (
                <Row className="min-w-0 flex-nowrap gap-2 overflow-x-auto px-px pt-2 pb-px">
                  {toast.actions.map((actionProps, idx) => (
                    // eslint-disable-next-line react/no-array-index-key -- a toast's actions are fixed at creation and never reordered, so the index is a stable key
                    <Button key={idx} {...actionProps} />
                  ))}
                </Row>
              )}
            </Col>
          </Row>
        )}
        <Toast.Close
          className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded border-none bg-transparent text-od-content-inverse-secondary hover:bg-od-bg-inverse-hover-primary hover:text-od-content-inverse-primary"
          aria-label="Close"
        >
          <IconX className="h-4 w-4" />
        </Toast.Close>
      </Toast.Content>
    </Toast.Root>
  ));
}

const TOAST_BACKGROUND_CLASSES: Record<string, string> = {
  success: 'bg-od-bg-success/5',
  error: 'bg-od-bg-error/5',
  warning: 'bg-od-bg-error/5',
  info: 'bg-od-bg-accent-brand/5',
};

function ToastBackground({ variant }: { variant?: AtomsToastVariant }) {
  const background = variant && TOAST_BACKGROUND_CLASSES[variant];
  return (
    <Box className={cn('absolute inset-0 rounded-xl bg-od-bg-inverse-primary/90', background)} />
  );
}

function ToastIcon({ variant }: { variant?: AtomsToastVariant }) {
  if (!variant || variant === 'default') return null;
  const variantConfig = ATOMS_TOAST_VARIANTS.variant[variant];
  if (!('icon' in variantConfig)) return null;
  const Icon = variantConfig.icon;
  return <Icon data-toast-icon className="translate-y-0.5 h-4 w-4 shrink-0" />;
}
